"use client";
import React, { createContext, useState, useContext, useEffect } from "react";
import { BASE_URL } from "../config/apiConfig";
import { toast } from "react-toastify";

export const BonusContext = createContext();

export const BonusProvider = ({ children }) => {
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ Token
  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ✅ Fetch all bonuses
  const fetchBonuses = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/bonuses`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch bonuses");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setBonuses(data.data || []);
      } else {
        setError(data.message || "Failed to fetch bonuses");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching bonuses");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Create bonus
  const createBonus = async (payload) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/bonuses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to create bonus",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchBonuses();
        toast.success(data.message || "Bonus created successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create bonus",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error creating bonus",
      };
    } finally {
      setLoading(false);
    }
  };

  // ✅ Update bonus
  const updateBonus = async (id, payload) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/bonuses/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update bonus",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchBonuses();
        toast.success(data.message || "Bonus updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update bonus",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating bonus",
      };
    } finally {
      setLoading(false);
    }
  };

  // ✅ Update status
  const updateBonusStatus = async (id, status, update_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/bonuses/${id}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status, update_audit_id }),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update bonus status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchBonuses();
        toast.success(data.message || "Bonus status updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update bonus status",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating bonus status",
      };
    }
  };

  // ✅ Delete bonus (soft delete)
  const deleteBonus = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/bonuses/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id }),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to delete bonus",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchBonuses();
        toast.success(data.message || "Bonus deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete bonus",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error deleting bonus",
      };
    }
  };

  // ✅ Load on mount
  useEffect(() => {
    const token = getToken();
    if (token) {
      fetchBonuses();
    }
  }, []);

  return (
    <BonusContext.Provider
      value={{
        bonuses,
        loading,
        error,
        fetchBonuses,
        createBonus,
        updateBonus,
        updateBonusStatus,
        deleteBonus,
        setBonuses,
      }}
    >
      {children}
    </BonusContext.Provider>
  );
};

// ✅ Hook
export const useBonuses = () => useContext(BonusContext);